import { create, StateCreator } from 'zustand'
import { persist } from 'zustand/middleware'

import { AddressInputAddress } from 'lib/components/Form/AddressInput/AddressInput'

import { BookingFlowProduct } from '@/app/book/stores/useCartStore'
import { TripType } from '@/app/book/stores/useShipmentStore'
import { generateOrderData } from '@/services/orderService'
import { ShipmentDatesSegment } from '@/types/shipment'

export type VirtualCartState = {
  data: VirtualCartData | null
  hasHydrated?: boolean
  values: Values
}

export interface Values {
  segments?: AddressInputAddress[]
  selectedProducts?: BookingFlowProduct[]
  shipment_dates?: ShipmentDatesSegment[]
  type?: TripType
}

export type VirtualCartData = Partial<Order>

export const initState: VirtualCartState = {
  data: null,
  hasHydrated: false,
  values: {},
}

type VirtualCartActions = {
  setHasHydrated: (state: boolean) => void
  setValues: (values: Values) => void
}

type VirtualCartStore = VirtualCartState & VirtualCartActions

const getData = (values: Values): VirtualCartData | null => {
  const { segments = [], selectedProducts = [], type } = values

  if (!segments.length) return null

  const shipment_dates =
    type === 'oneway'
      ? [values.shipment_dates?.[0] || ({} as ShipmentDatesSegment)]
      : [
          values.shipment_dates?.[0] || ({} as ShipmentDatesSegment),
          values.shipment_dates?.[1] || ({} as ShipmentDatesSegment),
        ]

  return generateOrderData({ segments, shipment_dates }, selectedProducts, [{}])
}

const virtualCartStoreCreator: StateCreator<VirtualCartStore> = (set) => ({
  ...initState,
  setHasHydrated: (hasHydrated) => set({ hasHydrated }),
  setValues: (values) => set({ data: getData(values), values }),
})

const useVirtualCartStore = create<VirtualCartStore>()(
  persist<VirtualCartStore>(virtualCartStoreCreator, {
    name: 'virtual-cart-store',
    onRehydrateStorage: () => (state) => state?.setHasHydrated(true),
  }) as StateCreator<VirtualCartStore>,
)

export default useVirtualCartStore
